import { Injectable } from '@angular/core';

import { BetslipBarConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { Store } from '@ngrx/store';
import { Observable, combineLatest, map, withLatestFrom } from 'rxjs';

import { pickWithErrorSelector } from '../../picks/selectors';
import {
    comboPickListCountSelector,
    hasComboPreventionErrorSelector,
    hasMinSelectionsBetbuilderError,
    hasPickLockedPreCheckErrorSelector,
    pickStatusChangeErrorSelector,
    selectedPicksByCurrentTypeSelector,
} from '../selectors';
import { BetBarErrorTracking } from './betslip-bar-tracking.service';
import { BetslipBarTranslations } from './betslip-bar.service';

export interface RemovablePickErrorModel {
    errorMessage: string | undefined;
    showUndo: boolean;
    hasError: boolean;
    trackError: BetBarErrorTracking | undefined;
    comboBetPickCount?: number;
}

@Injectable()
export class BetslipBarRemovePickService {
    private readonly lastPickState$ = this.store.select(pickWithErrorSelector);
    private readonly selectedPicks$ = this.store.select(selectedPicksByCurrentTypeSelector);
    private readonly betBuilderUnderMinimumSelections$ = this.store.select(hasMinSelectionsBetbuilderError);
    private readonly precheckError$ = this.store.select(hasPickLockedPreCheckErrorSelector);
    private readonly hasComboPreventionError$ = this.store.select(hasComboPreventionErrorSelector);
    private readonly pickStatusChangeError$ = this.store.select(pickStatusChangeErrorSelector);
    private readonly translations: BetslipBarTranslations;
    private comboBetPickCount$ = this.store.select(comboPickListCountSelector);

    constructor(
        private store: Store,
        { betslip: { betslipBar } }: Sitecore,
        private betslipBarConfig: BetslipBarConfig,
    ) {
        this.translations = betslipBar;
    }

    lastPickUndoState$ = this.lastPickState$.pipe(
        map((pick) => ({
            hasUndo: !!pick?.pickId && pick.hasError && !pick.previousHadError,
            hasError: pick?.hasError ?? false,
        })),
    );

    readonly errorModel$: Observable<RemovablePickErrorModel> = combineLatest([
        this.lastPickUndoState$,
        this.pickStatusChangeError$,
        this.betBuilderUnderMinimumSelections$,
        this.selectedPicks$,
    ]).pipe(
        withLatestFrom(this.precheckError$, this.hasComboPreventionError$, this.comboBetPickCount$),
        map(([[lastPickUndoState, pickStatusChangeError, betBuilderPickMissingLeg, _], precheckError, comboPreventionError, comboBetPickCount]) => {
            const showPickAvailabilityMessage = pickStatusChangeError && !precheckError;
            const showUndo = lastPickUndoState.hasUndo && !betBuilderPickMissingLeg && !showPickAvailabilityMessage;
            const hasError = showPickAvailabilityMessage || betBuilderPickMissingLeg || comboPreventionError || lastPickUndoState.hasError;

            if (!this.betslipBarConfig.isAngstromSGPEnabled) {
                return {
                    errorMessage: undefined,
                    showUndo: false,
                    hasError,
                    trackError: undefined,
                };
            }

            return {
                errorMessage: this.getErrorMessage(
                    showPickAvailabilityMessage,
                    lastPickUndoState.hasError && !lastPickUndoState.hasUndo,
                    betBuilderPickMissingLeg,
                    showUndo,
                ),
                showUndo,
                hasError,
                trackError: this.getErrorTracking(showPickAvailabilityMessage, lastPickUndoState, showUndo),
                comboBetPickCount,
            };
        }),
    );

    private getErrorMessage(
        showPickAvailabilityMessage: boolean,
        hasErrorCantUndo: boolean,
        betBuilderPickMissingLeg: boolean,
        showUndo: boolean,
    ): string | undefined {
        if (betBuilderPickMissingLeg) {
            return this.translations.BetBuilderMissingPicks;
        }

        if (showPickAvailabilityMessage) {
            return this.translations.ClosedOrLockedPick;
        }

        if (hasErrorCantUndo) {
            return this.translations.BetBuilderWarning;
        }
        if (showUndo) {
            return this.translations.LastLegUncombineable;
        }

        return undefined;
    }

    private getErrorTracking(
        availabilityChange: boolean,
        undoState: { hasUndo: boolean; hasError: boolean },
        showUndo: boolean,
    ): BetBarErrorTracking | undefined {
        if (availabilityChange) {
            return BetBarErrorTracking.AvailabilityChanged;
        }
        if (showUndo) {
            return BetBarErrorTracking.Undo;
        }
        if (undoState.hasError && !undoState.hasUndo) {
            return BetBarErrorTracking.UncombinableSelections;
        }

        return undefined;
    }
}
